/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * Crystal-side non-bonded kernel. Cloned from
 * src/models/NonBondedInteractions.ts so we can evolve the crystal MD
 * (PBC, Ewald, Buckingham, …) without touching the live molecule path.
 *
 * Differences from the molecule kernel:
 *   - No `radialBonds` field / `skipPair` bonded-pair filter.
 *     Crystals don't carry molecular-bond lists; intramolecular forces
 *     in a crystal come from the pair potential + (eventually) Ewald.
 *   - No gas/solid sigma hack. The molecule kernel zeros out the LJ
 *     well depth for cross-class pairs to stop noble-gas atoms from
 *     sticking to solids; that's an organic-chem teaching workaround
 *     that doesn't make physical sense for crystals.
 *   - Pair force routed through a `PairPotential` strategy. Default is
 *     `LennardJonesPotential` with Lorentz-Berthelot combining rules.
 *     Drop in Buckingham, Tersoff, etc. by subclassing the strategy.
 *   - Coulomb stays inline (every potential needs it the same way) and
 *     will be the first thing replaced by Ewald in the next PR.
 *
 * Everything else — the Verlet neighbor list, the rsq accumulation, the
 * virial bookkeeping — is reused verbatim. The crystal MD doesn't need
 * to reinvent neighbor lists.
 */

import { Atom } from '../Atom.ts';
import { Vector3 } from 'three';
import { Pair } from '../Pair.ts';
import { ZERO_TOLERANCE } from '../../constants.ts';
import { COULOMB_CONSTANT, GF_CONVERSION_CONSTANT } from '../physicalConstants.ts';
import { RadialBond } from '../RadialBond.ts';
import {
  BondOrderPotential,
  InterlayerLJ,
  LennardJonesPotential,
  ManyBodyPotential,
  PairPotential,
  shiftedForceLJ,
  ThreeBodyPotential,
} from './PairPotential.ts';
import { VirtualSite } from './VirtualSite.ts';
import { PeriodicBox } from './PeriodicBox.ts';
import { ChargeEntity, EwaldSummation } from './Ewald.ts';
import { getUffLj } from '../uffParameters.ts';

/**
 * Complementary error function — Abramowitz & Stegun 7.1.26, valid for
 * x ≥ 0 to ~1e-7. Used by the damped-shifted-force electrostatics; the
 * argument α·r is always non-negative.
 */
const erfc = (x: number): number => {
  const t = 1 / (1 + 0.3275911 * x);
  const y = t * (0.254829592 + t * (-0.284496736 + t * (1.421413741 + t * (-1.453152027 + t * 1.061405429))));
  return y * Math.exp(-x * x);
};

/** Guest-overlay sites at or below this ε (eV) are LJ placeholders and skipped. */
const INERT_EPS = 2e-4;

export class CrystalPairInteractions {
  atoms: Atom[] = [];
  /**
   * Intramolecular bonds. Bonded pairs are skipped by the LJ + Coulomb
   * kernel so the bonded-force loop (in CrystalDynamics.calculateForce)
   * doesn't double-count their interaction. Required for molecular
   * crystals (naphthalene, ice, sugars) where the molecule's covalent
   * skeleton must persist through the MD; the pair potential alone
   * would let the molecules dissociate at the first thermal kick.
   */
  radialBonds: RadialBond[] = [];
  /**
   * Virtual sites carrying charges but no mass / LJ — used by
   * 4-site water models (TIP4P/Ice) to place the negative pole on
   * an M-site offset from the oxygen. Set by CrystalDynamics; we
   * sweep these alongside real atoms in the Coulomb pass and
   * decompose each site's force back to its parent atoms.
   */
  virtualSites: VirtualSite[] = [];
  /**
   * Lennard-Jones cutoff in reduced (sigma) units. 2.0σ — for O-O that
   * is ~6.3 Å. The molecule kernel uses 2.5σ; 2.0σ still sits well past
   * the LJ minimum (2^(1/6)σ ≈ 1.12σ) so it captures essentially the
   * whole well, while trimming the neighbor list and the pair sweep.
   * Going much shorter (toward the well bottom) would truncate real
   * attraction and leave a large energy-conservation step at the cut.
   */
  rCutoff: number = 2.0;
  /** Visual van der Waals overlay cutoff (drawn lines), unrelated to forces. */
  vdwLineCutoff: number = 0.5;
  pairs: Pair[] = [];
  virialLJ: number = 0;
  virialEL: number = 0;

  /**
   * Periodic box. When set, the kernel switches to periodic boundaries:
   * every pair distance is taken as the minimum image, and Coulomb uses
   * the damped-shifted-force sum. When undefined, the kernel runs the
   * original finite (Verlet-neighbor-list) path.
   */
  box?: PeriodicBox;
  /** Squared interaction cutoff under PBC — half the smallest cell width. */
  private ljMaxCutoffSq = Infinity;
  /** Damped-shifted-force (DSF) electrostatics parameters. */
  private dsfCutoff = 0;
  private dsfAlpha = 0;
  private dsfEnergyShift = 0; // erfc(αRc)/Rc
  private dsfForceShift = 0; // erfc(αRc)/Rc² + (2α/√π)·exp(−α²Rc²)/Rc
  private dsfSelfCoef = 0; // erfc(αRc)/2Rc + α/√π — Wolf self-energy coefficient

  /**
   * Exact Ewald electrostatics. Configured alongside the DSF parameters
   * in setPeriodicBox(); used by computeCoulomb() when `useEwald` is on.
   * Ewald is size-independent (correct melting point) where DSF is
   * cutoff-limited; DSF is kept as the fallback.
   */
  private ewald?: EwaldSummation;
  /** Switch between exact Ewald (true) and the DSF approximation (false). */
  useEwald: boolean = true;

  /**
   * Pluggable pair potential. Default is LJ; the simulation controls
   * panel (forthcoming) will let the user swap to Buckingham etc. via
   * a per-crystal field-type tag.
   */
  potential: PairPotential = new LennardJonesPotential();
  /**
   * Many-body (EAM-family) potential. When set, the kernel dispatches
   * to a two-pass density/force sweep instead of using `potential` —
   * the single-pass PairPotential interface can't express the per-atom
   * embedding F(ρ_i) that EAM-family potentials carry.
   */
  manyBodyPotential?: ManyBodyPotential;
  /**
   * Three-body (covalent) potential. When set, the kernel dispatches to
   * `computeThreeBody()` — a pair pass over (i,j) plus a triple pass over
   * each central atom i's neighbor pairs (j,k). Covalent crystals (Si,
   * Ge, diamond, the III-Vs) need this: a pair-only or embedded-atom
   * potential cannot maintain the tetrahedral sp³ geometry that defines
   * the diamond structure — they all prefer close-packed.
   */
  threeBodyPotential?: ThreeBodyPotential;
  /**
   * Bond-order (Tersoff-family) potential. When set, the kernel
   * dispatches to `computeBondOrder()` — the energy of bond (i,j) is
   * not a sum over (i,j,k) triples but a function of i's FULL neighbor
   * environment, so the per-atom evaluation walks i's neighbors and
   * the potential applies all chain-rule forces internally.
   */
  bondOrderPotential?: BondOrderPotential;
  /**
   * Long-range van der Waals overlay for LAYERED covalent crystals
   * (graphite, h-BN). Runs as an ADDITIONAL pass alongside the bond-
   * order potential, NOT instead of it: bond-order handles in-plane
   * covalent bonding; this adds the interlayer LJ that bond-order
   * alone can't provide. A switching function in `InterlayerLJ.evaluate`
   * gates the LJ to zero below the bonded-region cutoff (~2.5 Å for C),
   * so the LJ never contaminates the covalent bond.
   */
  interlayerLJ?: InterlayerLJ;
  /** Scratch buffer: per-atom local density ρ_i (built in the density pass). */
  private rho: Float64Array = new Float64Array(0);
  /** Scratch buffer: per-atom F'(ρ_i) carried from embedding to the force pass. */
  private dFdRho: Float64Array = new Float64Array(0);
  /** Per-atom neighbor-list scratch for the bond-order kernel. */
  private boDx: Float64Array = new Float64Array(0);
  private boDy: Float64Array = new Float64Array(0);
  private boDz: Float64Array = new Float64Array(0);
  private boR: Float64Array = new Float64Array(0);
  private boNeighbours: Atom[] = [];

  // ── Guest–host non-bonded overlay ───────────────────────────────────
  // Guest molecules (Atom.guestId set) carry their own σ/ε/charge from the
  // guest library, but the host's PRIMARY potential is parameterized for
  // host species only: SW/Tersoff/EAM return nothing for a host–guest pair,
  // and those force fields bypass the generic LJ pass entirely — so a guest
  // in a covalent/metallic/ionic crystal feels NO force from the host and
  // drifts straight through. This overlay supplies the missing host–guest
  // (and guest–guest) van der Waals: an extra LJ pass over every pair that
  // involves a guest, using the guest's library σ/ε and a UFF σ/ε reference
  // for the host (whose stored σ/ε are clobbered to a neighbor-list length
  // by the covalent/metallic fields). Coulomb is already handled uniformly
  // by computeCoulomb() (it keys off charge + moleculeName, not the primary
  // potential), so only the LJ part is added here.
  //
  // It runs ONLY when the primary potential doesn't already cover guest
  // pairs (i.e. not a plain Lennard-Jones host) — see guestPairsCovered() —
  // so an LJ host (noble gas, water, generic-LJ fallback), where the main
  // pass already integrates the guest via its own σ/ε, is left untouched
  // and never double-counted.
  private guestIndices: number[] = [];
  /** Effective LJ σ per atom for the overlay: guest → library; host → UFF. */
  private guestLJSigma: Float64Array = new Float64Array(0);
  /** Effective LJ ε per atom for the overlay (0 ⇒ excluded from the overlay). */
  private guestLJEps: Float64Array = new Float64Array(0);

  private updateList: boolean = true;
  private neighborList: number[] = [];
  private pointer: number[] = [];
  /** Atom count, cached for the bonded-pair key arithmetic. */
  private atomCount: number = 0;
  /**
   * Flat set of bonded atom pairs, keyed `lo·N + hi`. skipPair() is
   * called O(N²) times per force evaluation; consulting this set is
   * O(1), versus scanning the whole bond list (which makes the kernel
   * O(N²·N_bonds) and dominates the cost at large supercells).
   */
  private bondedPairKeys: Set<number> = new Set<number>();
  /** Largest σ among the atoms — decides whether the PBC LJ list has a skin. */
  private maxSigma: number = 1;
  /** Countdown to the next PBC LJ neighbor-list rebuild. */
  private ljPbcListAge: number = 0;
  private lastPositions: Vector3[] = [];
  /** Outer skin radius for the Verlet list (in sigma units). */
  private rList: number = this.rCutoff + 1;
  /**
   * Cached charge-entity list for the Coulomb sweep. Membership (which
   * atoms / virtual sites carry charge), per-entity charge and the
   * applyForce closures are all fixed for this kernel's lifetime, and
   * `position` is a live Vector3 reference — so the list is built once
   * and reused, instead of reallocating ~N objects + closures on every
   * force evaluation. (`dead` is captured at build time; a Cut mid-run
   * leaves it stale, which at worst skips a dead-dead pair optimization.)
   */
  private chargeEntities: ChargeEntity[] | null = null;
  /** Per-entity updaters that re-read `fixed` / `removed` on every gather. */
  private chargeEntityRefresh: Array<() => void> = [];
  /**
   * Coulomb energy of an ALL-FIXED charge set, keyed by a position checksum.
   * When every charged entity is fixed (a frozen zeolite framework holding
   * neutral guests — runSievePermeation), electrostatics moves nothing, so
   * the Ewald sum (≈ 75 % of the step on CHA 2×2×1) is computed once and its
   * energy reused until a charge is released or a fixed atom is moved.
   */
  private frozenCoulomb: { checksum: number; count: number; energy: number } | null = null;

  constructor(atoms: Atom[], radialBonds: RadialBond[] = []) {
    this.atoms = atoms;
    this.radialBonds = radialBonds;
    const n = atoms.length;
    this.atomCount = n;
    this.lastPositions = new Array<Vector3>(n);
    for (let i = 0; i < n; i++) {
      this.lastPositions[i] = new Vector3();
    }
    this.neighborList = new Array<number>(Math.round((n * n) / 2));
    this.pointer = new Array<number>(n);
    // Precompute the bonded-pair lookup once — see bondedPairKeys.
    this.setBondedPairs(radialBonds);
    let maxSig = 0;
    for (const a of atoms) if (a.sigma > maxSig) maxSig = a.sigma;
    this.maxSigma = maxSig > 0 ? maxSig : 1;

    // Build the guest-overlay tables. Guest atoms keep their library σ/ε
    // (set by buildGuestAtoms and preserved by every FF's guest skip);
    // host atoms get a UFF σ/ε reference, since the covalent/metallic FFs
    // overwrite atom.sigma with a neighbor-list length and never set a
    // physical ε. A host element without UFF parameters gets ε = 0, which
    // excludes it from the overlay (the guest passes through it — a rare
    // fallback, better than a wrong wall).
    this.guestLJSigma = new Float64Array(n);
    this.guestLJEps = new Float64Array(n);
    for (let i = 0; i < n; i++) {
      const a = atoms[i];
      if (a.guestId !== undefined) {
        this.guestIndices.push(i);
        this.guestLJSigma[i] = a.sigma;
        this.guestLJEps[i] = a.epsilon;
      } else {
        const uff = getUffLj(a.elementSymbol);
        this.guestLJSigma[i] = uff ? uff.sigma : a.sigma;
        this.guestLJEps[i] = uff ? uff.epsilon : 0;
      }
    }
  }

  /**
   * Override the Lennard-Jones σ (Å) / ε (eV) that HOST atoms of one element
   * present to guests in the overlay (default: UFF). Set by a force field
   * whose framework needs its own adsorption parameters (BKS silica →
   * TraPPE-zeo, see IonicPack.guestHostLJ).
   */
  setGuestHostLJ(element: string, sigma: number, epsilon: number): void {
    for (let i = 0; i < this.atoms.length; i++) {
      const a = this.atoms[i];
      if (a.guestId !== undefined || a.elementSymbol !== element) continue;
      this.guestLJSigma[i] = sigma;
      this.guestLJEps[i] = epsilon;
    }
    this.overlayList = null;
  }

  /**
   * True when the primary potential already integrates guest pairs — i.e.
   * the host runs the generic Lennard-Jones pair pass, which reads each
   * atom's own σ/ε and so naturally includes guests (noble-gas, water,
   * generic-LJ fallback). For SW/Tersoff (three-body / bond-order), EAM
   * (many-body), and Buckingham/Morse pair potentials (which return null
   * for an untabulated host–guest pair), guests are NOT covered and the
   * overlay must supply their van der Waals.
   */
  // When the overlay integrates guest pairs, the primary potential must NOT see
  // them too: it is keyed by element, so a guest CO₂'s oxygen got silica's BKS
  // Si–O / O–O Buckingham (C = 133 eV·Å⁶) on top of the TraPPE-zeo overlay —
  // ~−7 kJ/mol per Si neighbor — and stuck to the walls (MD hopped ~40× slower
  // than the free-energy rate for the intended potential, 2026-10-15).
  private overlayOwnsGuests = false;
  private evaluatePrimary(ai: Atom, aj: Atom, rsq: number, rCutSq: number) {
    if (this.overlayOwnsGuests && (ai.guestId !== undefined || aj.guestId !== undefined)) return null;
    return this.potential.evaluate(ai, aj, rsq, rCutSq);
  }

  private guestPairsCovered(): boolean {
    return (
      !this.manyBodyPotential &&
      !this.threeBodyPotential &&
      !this.bondOrderPotential &&
      this.potential.name === 'Lennard-Jones'
    );
  }

  /** Force the PBC LJ neighbor list to rebuild on the next force pass. */
  invalidateNeighborList(): void {
    this.ljPbcListAge = 0;
    this.updateList = true;
    this.overlayList = null;
    this.boParticipants = null;
  }

  /**
   * Re-arms the neighbor-list refresh flag when the largest atomic
   * displacement since the last refresh approaches the Verlet skin.
   * Same exit criterion the molecule kernel uses — both are normalized
   * by sigma so this is unit-consistent across potentials.
   */
  checkNeighborList() {
    let max = 0;
    for (const [i, a] of this.atoms.entries()) {
      if (a.fixed) continue;
      max = Math.max(max, Math.abs(a.position.x - this.lastPositions[i].x) / a.sigma);
      max = Math.max(max, Math.abs(a.position.y - this.lastPositions[i].y) / a.sigma);
      max = Math.max(max, Math.abs(a.position.z - this.lastPositions[i].z) / a.sigma);
    }
    max = 2 * Math.sqrt(3.0 * max * max);
    this.updateList = max > this.rList - this.rCutoff;
  }

  /**
   * Single-pair skip rule. Skip if:
   *   - either atom has been `removed` by a Cut action (those atoms
   *     have left the system; they shouldn't exert or receive forces),
   *   - both atoms are fixed (no movement, no point integrating),
   *   - or the two atoms are connected by a radial bond (the bonded-
   *     force loop in CrystalDynamics handles those — don't let the
   *     LJ kernel re-pull them and double-count).
   * Angular/torsional bonds aren't skipped here because they involve
   * 3+ atoms and the pair kernel is, by definition, pairwise; the
   * molecule kernel uses the same rule.
   */
  /**
   * (Re)build the bonded-pair exclusion set from `radialBonds`. Called by
   * the constructor, and again by a force field that trims the scaffold's
   * bonds after the fact (a capped covalent nanostructure keeps only its
   * X–H bonds harmonic — the heavy–heavy pairs must then be visible to the
   * SW pair pass again, or the core has no cohesion).
   */
  setBondedPairs(radialBonds: RadialBond[]): void {
    const n = this.atomCount;
    this.bondedPairKeys = new Set<number>();
    const indexOf = new Map<Atom, number>();
    for (let i = 0; i < n; i++) indexOf.set(this.atoms[i], i);
    for (const rb of radialBonds) {
      const a = indexOf.get(rb.atom1);
      const b = indexOf.get(rb.atom2);
      if (a === undefined || b === undefined) continue;
      const lo = Math.min(a, b);
      const hi = Math.max(a, b);
      this.bondedPairKeys.add(lo * n + hi);
    }
  }

  skipPair(i: number, j: number): boolean {
    if (this.atoms[i].removed || this.atoms[j].removed) return true;
    if (this.atoms[i].fixed && this.atoms[j].fixed) return true;
    // Same-molecule exclusion: any pair of atoms tagged with the same
    // (non-empty) moleculeName is intramolecular and shouldn't get
    // pair-LJ. This catches 1-2 bonded pairs PLUS 1-3 / 1-4 pairs
    // that the bondedPairKeys set doesn't track. Critical for CO₂
    // (intramolecular O-O at 2.298 Å is on the LJ repulsive wall
    // with σ_O = 3.03; would contribute +0.6 eV per pair); harmless
    // for water (H σ ≈ 0.4 makes intramolecular H-H LJ negligible).
    // Atomic crystals (metals, noble gases) and ionic crystals get
    // one-atom-per-"molecule" tags from buildCrystalScaffold, so this
    // check never fires for them.
    const ai = this.atoms[i];
    const aj = this.atoms[j];
    if (ai.moleculeName && ai.moleculeName === aj.moleculeName) return true;
    // Bonded pair? O(1) set lookup on the lo·N + hi key.
    const lo = i < j ? i : j;
    const hi = i < j ? j : i;
    return this.bondedPairKeys.has(lo * this.atomCount + hi);
  }

  /**
   * Configure periodic boundaries. The interaction cutoff is set just
   * under half the smallest cell width — the hard limit of the
   * minimum-image convention (beyond L/2 an atom would interact with
   * two images of the same neighbor). The damped-shifted-force
   * electrostatics damping α is calibrated to that cutoff so the
   * truncation error stays small; a larger supercell → larger cutoff →
   * less damping → more accurate long-range electrostatics.
   */
  setPeriodicBox(box: PeriodicBox): void {
    this.box = box;
    const cutoff = 0.49 * box.minWidth;
    this.ljMaxCutoffSq = cutoff * cutoff;
    this.dsfCutoff = cutoff;
    this.dsfAlpha = 2.0 / cutoff;
    const a = this.dsfAlpha;
    const erfcARc = erfc(a * cutoff);
    const expARc = Math.exp(-a * a * cutoff * cutoff);
    const twoAOverSqrtPi = (2 * a) / Math.sqrt(Math.PI);
    this.dsfEnergyShift = erfcARc / cutoff;
    this.dsfForceShift = erfcARc / (cutoff * cutoff) + (twoAOverSqrtPi * expARc) / cutoff;
    this.dsfSelfCoef = erfcARc / (2 * cutoff) + a / Math.sqrt(Math.PI);
    // Exact Ewald — built from the same box. computeCoulomb() uses it
    // when useEwald is on (the default), DSF otherwise.
    this.ewald = new EwaldSummation();
    this.ewald.configure(box);
  }

  /**
   * Override the Ewald reciprocal-space multiple-time-step stride. The
   * default (3) holds the smooth reciprocal force constant for two of
   * every three steps — correct and fast for dynamics, but it makes a
   * finite-difference force check read a STALE reciprocal term, since
   * that check calls computeCoulomb() repeatedly at perturbed
   * geometries. Pass 1 to recompute Ewald in full on every call. Call
   * after setPeriodicBox(), which is what builds the Ewald instance.
   */
  setReciprocalStride(stride: number): void {
    if (this.ewald) this.ewald.reciprocalStride = stride;
  }

  compute(): number {
    if (this.atoms.length === 0) return 0;
    this.overlayOwnsGuests = this.guestIndices.length > 0 && !this.guestPairsCovered();
    for (const a of this.atoms) {
      a.force?.set(0, 0, 0);
    }
    this.virialLJ = 0;
    this.virialEL = 0;
    let energy: number;
    if (this.bondOrderPotential && this.box) {
      // Bond-order (Tersoff-family) covalent kernel — per-atom evaluation
      // with full chain-rule forces over the b_ij environment.
      energy = this.computeBondOrder();
    } else if (this.threeBodyPotential && this.box) {
      // Three-body (Stillinger-Weber) covalent kernel — pair pass +
      // triple pass with an explicit angular penalty.
      energy = this.computeThreeBody();
    } else if (this.manyBodyPotential && this.box) {
      // Many-body (EAM-family) two-pass kernel — used when the metallic
      // FF installs e.g. SuttonChenPotential on top of PBC.
      energy = this.computeManyBody();
    } else if (this.potential.name === 'none') {
      // NullPairPotential (membrane cell): no primary pairs at all — skip the
      // O(N²) pass that would evaluate every pair just to get nothing back.
      energy = 0;
    } else {
      energy = this.box ? this.computeLennardJonesPBC() : this.computeLennardJonesFinite();
    }
    // Interlayer LJ overlay — always runs when set, on top of whatever
    // the primary potential pass already produced. Adds long-range vdW
    // for layered covalent crystals on top of the in-plane Tersoff.
    // CAUTION: don't pair this with the standard LJ-PBC path (the
    // dispatch's `else` branch), or LJ would be double-counted. In
    // practice the layered FF sets bondOrderPotential (Tersoff) + this,
    // and the force-check oracle sets this with ai.epsilon = 0 so LJ-PBC
    // contributes zero.
    if (this.interlayerLJ && this.box) {
      energy += this.computeInterlayerLJ();
    }
    // Guest–host (and guest–guest) van der Waals overlay — only when the
    // primary potential doesn't already integrate guest pairs. Without
    // this, guests float through covalent/metallic/ionic hosts.
    if (this.guestIndices.length > 0 && !this.guestPairsCovered()) {
      energy += this.computeGuestHostLJ();
    }
    energy += this.computeCoulomb();
    return energy;
  }

  /**
   * Guest–host (and guest–guest) Lennard-Jones overlay. For each guest
   * atom, sum a shifted-force LJ against every non-same-guest atom, using
   * the per-atom effective σ/ε tables (guest: library; host: UFF). Mirrors
   * the force/energy bookkeeping of the main LJ pass: `fScalar·(rᵢ−rⱼ)` is
   * divided by each atom's mass so atom.force stores acceleration, and the
   * pair virial feeds the pressure readout.
   *
   * O(N_guest · N) brute force — guest counts are tiny (a handful of
   * atoms), so no neighbor list is warranted. Each pair is visited once:
   * guest–host pairs only iterate from the guest side; guest–guest pairs
   * are gated to j > i so they aren't double-counted (and double-forced).
   */
  // Verlet list for the guest overlay: for each active guest atom, the atoms
  // that pass the static pair filters and sit within the longest overlay cutoff
  // plus a skin. Rebuilt when any moving atom has drifted half the skin. Before
  // it every guest atom scanned every atom every step — in a membrane cell
  // (60 molecules over a 240-carbon sheet) that was the most expensive term.
  private overlayList: number[][] | null = null;
  private overlayListOwner: number[] = [];
  private overlayRefPos: Float64Array = new Float64Array(0);
  private static readonly OVERLAY_SKIN = 2.0;

  private overlayReach(): number {
    let maxSig = 0;
    for (let j = 0; j < this.atoms.length; j++) {
      if (this.guestLJEps[j] > INERT_EPS && this.guestLJSigma[j] > maxSig) maxSig = this.guestLJSigma[j];
    }
    return this.rCutoff * maxSig;
  }

  private overlayListStale(): boolean {
    const n = this.atoms.length;
    if (!this.overlayList || this.overlayRefPos.length !== 3 * n) return true;
    const limitSq = (0.5 * CrystalPairInteractions.OVERLAY_SKIN) ** 2;
    const v = new Vector3();
    for (let j = 0; j < n; j++) {
      const a = this.atoms[j];
      if (a.fixed) continue;
      v.set(
        a.position.x - this.overlayRefPos[3 * j],
        a.position.y - this.overlayRefPos[3 * j + 1],
        a.position.z - this.overlayRefPos[3 * j + 2],
      );
      if (this.box) this.box.minimumImage(v);
      if (v.lengthSq() > limitSq) return true;
    }
    return false;
  }

  private rebuildOverlayList(): void {
    const n = this.atoms.length;
    const box = this.box;
    const reachSq = (this.overlayReach() + CrystalPairInteractions.OVERLAY_SKIN) ** 2;
    const ljMaxSq = this.ljMaxCutoffSq;
    const v = new Vector3();
    this.overlayRefPos = new Float64Array(3 * n);
    for (let j = 0; j < n; j++) {
      const p = this.atoms[j].position;
      this.overlayRefPos[3 * j] = p.x;
      this.overlayRefPos[3 * j + 1] = p.y;
      this.overlayRefPos[3 * j + 2] = p.z;
    }
    this.overlayList = [];
    this.overlayListOwner = [];
    for (const i of this.guestIndices) {
      const ai = this.atoms[i];
      if (ai.removed || this.guestLJEps[i] <= INERT_EPS) continue;
      const gid = ai.guestId;
      const list: number[] = [];
      for (let j = 0; j < n; j++) {
        if (j === i) continue;
        const aj = this.atoms[j];
        if (aj.removed) continue;
        // Same guest molecule → intramolecular, held by its own bonds /
        // rigid constraints; never an overlay pair.
        if (aj.guestId !== undefined && aj.guestId === gid) continue;
        // Guest–guest pair: count once (j > i) so it isn't double-applied.
        if (aj.guestId !== undefined && j < i) continue;
        if (ai.fixed && aj.fixed) continue;
        if (this.guestLJEps[j] <= INERT_EPS) continue;
        v.subVectors(ai.position, aj.position);
        if (box) box.minimumImage(v);
        const rsq = v.lengthSq();
        if (rsq > reachSq || rsq > ljMaxSq) continue;
        list.push(j);
      }
      this.overlayListOwner.push(i);
      this.overlayList.push(list);
    }
  }

  private computeGuestHostLJ(): number {
    const box = this.box;
    const rCutoffSq = this.rCutoff * this.rCutoff; // reduced (σ) units
    const ljMaxSq = this.ljMaxCutoffSq;
    let energy = 0;
    const v = new Vector3();
    if (this.overlayListStale()) this.rebuildOverlayList();
    const owners = this.overlayListOwner;
    const lists = this.overlayList!;
    for (let k = 0; k < owners.length; k++) {
      const i = owners[k];
      const ai = this.atoms[i];
      if (ai.removed) continue;
      // ε ≤ 2·10⁻⁴ eV marks an LJ-INERT placeholder site (the hydrogens of the
      // united-atom methane, water H): never in the list.
      const epsI = this.guestLJEps[i];
      const sigI = this.guestLJSigma[i];
      const invMi = 1 / ai.mass;
      const fi = ai.force;
      for (const j of lists[k]) {
        const aj = this.atoms[j];
        if (aj.removed) continue;
        const epsJ = this.guestLJEps[j];
        v.subVectors(ai.position, aj.position);
        if (box) box.minimumImage(v);
        const rsq = v.lengthSq();
        if (rsq > ljMaxSq || rsq < 1e-8) continue;
        const sigmaMean = 0.5 * (sigI + this.guestLJSigma[j]);
        const rCutSq = rCutoffSq * sigmaMean * sigmaMean;
        if (rsq >= rCutSq) continue;
        const epsilonMean = Math.sqrt(epsI * epsJ);
        const result = shiftedForceLJ(sigmaMean, epsilonMean, rsq, rCutSq);
        energy += result.energy;
        this.virialLJ += result.virial;
        const gx = result.fScalar * v.x;
        const gy = result.fScalar * v.y;
        const gz = result.fScalar * v.z;
        if (fi) {
          fi.x += gx * invMi;
          fi.y += gy * invMi;
          fi.z += gz * invMi;
        }
        const fj = aj.force;
        if (fj) {
          const invMj = 1 / aj.mass;
          fj.x -= gx * invMj;
          fj.y -= gy * invMj;
          fj.z -= gz * invMj;
        }
      }
    }
    return energy;
  }

  // Verlet list for the interlayer LJ overlay: for each atom i, the j > i
  // within cutoff + skin, rebuilt once any atom has moved half the skin
  // since the list was taken. Before this the overlay scanned every atom
  // pair each step — 460,000 distance tests for a 960-atom triple-wall
  // nanotube, 85 % of its step time and the whole of its 15 s load-time
  // relaxation. Rebuilds are rare (an atom at 300 K moves ~0.01 Å a step).
  private iljCandidates: Int32Array[] = [];
  private iljRefPos: Float64Array = new Float64Array(0);
  /** Per-atom row in the potential's element-pair table; −1 = no parameters, never listed. */
  private iljElem: Int32Array = new Int32Array(0);
  private static readonly ILJ_SKIN = 1.0;

  /** The potential the list's element rows belong to; a swapped potential forces a rebuild. */
  private iljListOwner: InterlayerLJ | undefined;

  private interlayerListStale(): boolean {
    const n = this.atoms.length;
    if (this.iljRefPos.length !== 3 * n || this.iljListOwner !== this.interlayerLJ) return true;
    const limitSq = (0.5 * CrystalPairInteractions.ILJ_SKIN) ** 2;
    const v = new Vector3();
    for (let i = 0; i < n; i++) {
      const p = this.atoms[i].position;
      v.set(p.x - this.iljRefPos[3 * i], p.y - this.iljRefPos[3 * i + 1], p.z - this.iljRefPos[3 * i + 2]);
      this.box!.minimumImage(v);
      if (v.lengthSq() > limitSq) return true;
    }
    return false;
  }

  private rebuildInterlayerList(rCutoff: number): void {
    const n = this.atoms.length;
    const box = this.box!;
    const reach = (rCutoff + CrystalPairInteractions.ILJ_SKIN) ** 2;
    const v = new Vector3();
    const lj = this.interlayerLJ!;
    this.iljListOwner = lj;
    this.iljRefPos = new Float64Array(3 * n);
    this.iljCandidates = new Array<Int32Array>(n);
    this.iljElem = new Int32Array(n);
    for (let i = 0; i < n; i++) this.iljElem[i] = lj.indexFor(this.atoms[i].elementSymbol);
    const empty = new Int32Array(0);
    const scratch: number[] = [];
    for (let i = 0; i < n; i++) {
      const ai = this.atoms[i];
      this.iljRefPos[3 * i] = ai.position.x;
      this.iljRefPos[3 * i + 1] = ai.position.y;
      this.iljRefPos[3 * i + 2] = ai.position.z;
      if (this.iljElem[i] < 0) {
        this.iljCandidates[i] = empty;
        continue;
      }
      scratch.length = 0;
      for (let j = i + 1; j < n; j++) {
        if (this.iljElem[j] < 0) continue;
        v.subVectors(ai.position, this.atoms[j].position);
        box.minimumImage(v);
        if (v.lengthSq() <= reach) scratch.push(j);
      }
      this.iljCandidates[i] = Int32Array.from(scratch);
    }
  }

  /**
   * Long-range LJ overlay for layered covalent crystals — runs AFTER
   * the bond-order pass to add the interlayer vdW cohesion that
   * Tersoff can't provide. Walks the Verlet list above with minimum-image
   * displacements; the switch inside `InterlayerLJ.evaluate` gates it to
   * zero in the bonded region. `removed` and fixed–fixed pairs are skipped
   * at evaluation time, so a Cut or a pin needs no list rebuild.
   */
  private computeInterlayerLJ(): number {
    const n = this.atoms.length;
    const box = this.box!;
    const lj = this.interlayerLJ!;
    const rCutoffSq = lj.rCutoff * lj.rCutoff;
    if (this.interlayerListStale()) this.rebuildInterlayerList(lj.rCutoff);
    let energy = 0;
    const v = new Vector3();
    for (let i = 0; i < n - 1; i++) {
      const ai = this.atoms[i];
      if (ai.removed) continue;
      const fi = ai.force;
      const invMi = 1 / ai.mass;
      const list = this.iljCandidates[i];
      for (let k = 0; k < list.length; k++) {
        const aj = this.atoms[list[k]];
        if (aj.removed) continue;
        if (ai.fixed && aj.fixed) continue;
        v.subVectors(ai.position, aj.position);
        box.minimumImage(v);
        const rsq = v.lengthSq();
        if (rsq >= rCutoffSq) continue;
        const r = Math.sqrt(rsq);
        if (!lj.evaluateIndexed(this.iljElem[i], this.iljElem[list[k]], r, rsq)) continue;
        energy += lj.energyOut;
        const gx = lj.fScalarOut * v.x;
        const gy = lj.fScalarOut * v.y;
        const gz = lj.fScalarOut * v.z;
        if (fi) {
          fi.x += gx * invMi;
          fi.y += gy * invMi;
          fi.z += gz * invMi;
        }
        const fj = aj.force;
        if (fj) {
          const invMj = 1 / aj.mass;
          fj.x -= gx * invMj;
          fj.y -= gy * invMj;
          fj.z -= gz * invMj;
        }
      }
    }
    return energy;
  }

  /**
   * Bond-order (Tersoff-family) kernel — per-atom evaluation. The
   * energy of bond (i, j) couples to b_ij, which itself is a sum over
   * every other atom k in i's coordination shell; you can't separate
   * the bond from its environment. So the kernel just assembles each
   * atom's neighbor list and hands it to the potential, which walks
   * the (i, j) bonds AND applies all the chain-rule forces (on i, j,
   * and every k whose presence shapes b_ij) internally.
   *
   * Brute O(N²) neighbor scan, then O(Z²) per atom for the pair pass
   * (each bond's ζ_ij sum is O(Z)). The kernel scratch arrays for
   * displacements + indices are resized once per atom-count change.
   */
  // Bond-order participants (non-guest atoms the potential covers) and a
  // Verlet list over them: candidates within cutoff + skin, rebuilt once any
  // participant has moved half the skin. Before this the kernel scanned every
  // atom pair, gas included — 420² distance tests a step for a membrane cell
  // whose gas never enters the bond-order sum.
  private boParticipants: number[] | null = null;
  private boCandidates: number[][] = [];
  private boRefPos: Float64Array = new Float64Array(0);
  private static readonly BO_SKIN = 1.0;

  private rebuildBondOrderList(cutoff: number): void {
    const box = this.box!;
    const bo = this.bondOrderPotential!;
    if (!this.boParticipants) {
      this.boParticipants = [];
      for (let i = 0; i < this.atoms.length; i++) {
        const a = this.atoms[i];
        if (a.guestId !== undefined) continue;
        if (bo.handlesElement && !bo.handlesElement(a.elementSymbol)) continue;
        this.boParticipants.push(i);
      }
    }
    const P = this.boParticipants;
    const reach = (cutoff + CrystalPairInteractions.BO_SKIN) ** 2;
    const v = new Vector3();
    this.boCandidates = P.map(() => []);
    this.boRefPos = new Float64Array(3 * P.length);
    for (let p = 0; p < P.length; p++) {
      const ai = this.atoms[P[p]];
      this.boRefPos[3 * p] = ai.position.x;
      this.boRefPos[3 * p + 1] = ai.position.y;
      this.boRefPos[3 * p + 2] = ai.position.z;
      for (let q = 0; q < P.length; q++) {
        if (q === p) continue;
        v.subVectors(ai.position, this.atoms[P[q]].position);
        box.minimumImage(v);
        if (v.lengthSq() <= reach) this.boCandidates[p].push(P[q]);
      }
    }
  }

  private bondOrderListStale(): boolean {
    const P = this.boParticipants;
    if (!P || this.boRefPos.length !== 3 * P.length) return true;
    const limitSq = (0.5 * CrystalPairInteractions.BO_SKIN) ** 2;
    const v = new Vector3();
    for (let p = 0; p < P.length; p++) {
      const a = this.atoms[P[p]].position;
      v.set(a.x - this.boRefPos[3 * p], a.y - this.boRefPos[3 * p + 1], a.z - this.boRefPos[3 * p + 2]);
      this.box!.minimumImage(v);
      if (v.lengthSq() > limitSq) return true;
    }
    return false;
  }

  private computeBondOrder(): number {
    const n = this.atoms.length;
    const box = this.box!;
    const bo = this.bondOrderPotential!;
    const cutoff = bo.cutoff;
    const cutoffSq = cutoff * cutoff;
    if (this.bondOrderListStale()) this.rebuildBondOrderList(cutoff);
    const P = this.boParticipants!;

    // Resize neighbor-list scratch when N grows. We never shrink — a
    // typical session opens one crystal of a given size and runs it.
    if (this.boDx.length < n) {
      this.boDx = new Float64Array(n);
      this.boDy = new Float64Array(n);
      this.boDz = new Float64Array(n);
      this.boR = new Float64Array(n);
      this.boNeighbours = new Array<Atom>(n);
    }

    let energy = 0;
    const v = new Vector3();
    for (let p = 0; p < P.length; p++) {
      const ai = this.atoms[P[p]];
      if (ai.removed) continue;
      // Assemble i's in-cutoff neighbors (minimum-image displacements).
      let count = 0;
      for (const j of this.boCandidates[p]) {
        const aj = this.atoms[j];
        if (aj.removed) continue;
        v.subVectors(ai.position, aj.position);
        box.minimumImage(v);
        const rsq = v.lengthSq();
        if (rsq > cutoffSq) continue;
        this.boDx[count] = v.x;
        this.boDy[count] = v.y;
        this.boDz[count] = v.z;
        this.boR[count] = Math.sqrt(rsq);
        this.boNeighbours[count] = aj;
        count++;
      }
      energy += bo.evaluateAtom(ai, this.boNeighbours, this.boDx, this.boDy, this.boDz, this.boR, count);
    }
    return energy;
  }

  /**
   * Three-body (covalent) kernel — pair pass + triple pass.
   *
   *   Pass 1 (pairs):   for each pair (i,j) within cutoff, evaluate the
   *                     two-body term V₂(r_ij) and apply pair forces.
   *   Pass 2 (triples): for each atom i, collect its neighbors within
   *                     cutoff, then walk each unordered pair (j,k) of
   *                     them — i is the central atom of the triple. The
   *                     three-body term V₃(r_ij, r_ik, θ_jik) supplies
   *                     forces on i, j, and k (Newton's third law holds
   *                     within the triple).
   *
   * Brute O(N² + N·Z²) — no cell list yet, same as the EAM path. For the
   * supercells the app runs (≲ few hundred atoms) this is fast enough;
   * cell-listable later if covalent supercells grow large.
   *
   * Bonded-pair skip is NOT applied to V₂: covalent crystals carry no
   * separate harmonic bonds (the SW V₂ IS the bond), so bondedPairKeys
   * is empty in this path. Triples are emitted regardless of any bond
   * graph — the central atom owns the angular term unconditionally.
   */
  private computeThreeBody(): number {
    const n = this.atoms.length;
    const box = this.box!;
    const tb = this.threeBodyPotential!;
    const cutoff = tb.cutoff;
    const cutoffSq = cutoff * cutoff;
    const ljMaxSq = this.ljMaxCutoffSq;
    let energy = 0;

    // Pair pass — V₂ contributions and pair forces.
    const v = new Vector3();
    for (let i = 0; i < n - 1; i++) {
      const ai = this.atoms[i];
      const invMi = 1 / ai.mass;
      const fi = ai.force;
      for (let j = i + 1; j < n; j++) {
        if (this.skipPair(i, j)) continue;
        const aj = this.atoms[j];
        v.subVectors(ai.position, aj.position);
        box.minimumImage(v);
        const rsq = v.lengthSq();
        if (rsq > ljMaxSq || rsq > cutoffSq) continue;
        const result = tb.pairTerm(ai, aj, rsq);
        if (!result) continue;
        energy += result.energy;
        const invMj = 1 / aj.mass;
        const gx = result.fScalar * v.x;
        const gy = result.fScalar * v.y;
        const gz = result.fScalar * v.z;
        if (fi) {
          fi.x += gx * invMi;
          fi.y += gy * invMi;
          fi.z += gz * invMi;
        }
        const fj = aj.force;
        if (fj) {
          fj.x -= gx * invMj;
          fj.y -= gy * invMj;
          fj.z -= gz * invMj;
        }
      }
    }

    // Triple pass — V₃ contributions over (i, j, k) with i central.
    // Per-atom scratch: indices of in-cutoff neighbors and their
    // minimum-image displacement vectors dij = rᵢ − rⱼ. Reused (allocated
    // once at the outer loop's start) so a 100-atom supercell allocates
    // five arrays total, not 500.
    const neighborIdx: number[] = [];
    const neighborDx: number[] = [];
    const neighborDy: number[] = [];
    const neighborDz: number[] = [];
    const neighborR: number[] = [];
    for (let i = 0; i < n; i++) {
      const ai = this.atoms[i];
      if (ai.removed) continue;
      neighborIdx.length = 0;
      neighborDx.length = 0;
      neighborDy.length = 0;
      neighborDz.length = 0;
      neighborR.length = 0;
      for (let j = 0; j < n; j++) {
        if (j === i) continue;
        const aj = this.atoms[j];
        if (aj.removed) continue;
        v.subVectors(ai.position, aj.position);
        box.minimumImage(v);
        const rsq = v.lengthSq();
        if (rsq > cutoffSq) continue;
        neighborIdx.push(j);
        neighborDx.push(v.x);
        neighborDy.push(v.y);
        neighborDz.push(v.z);
        neighborR.push(Math.sqrt(rsq));
      }

      const invMi = 1 / ai.mass;
      const fi = ai.force;
      const m = neighborIdx.length;
      for (let nj = 0; nj < m - 1; nj++) {
        const jIdx = neighborIdx[nj];
        const aj = this.atoms[jIdx];
        const invMj = 1 / aj.mass;
        const fj = aj.force;
        const dijx = neighborDx[nj];
        const dijy = neighborDy[nj];
        const dijz = neighborDz[nj];
        const rij = neighborR[nj];
        for (let nk = nj + 1; nk < m; nk++) {
          const kIdx = neighborIdx[nk];
          const ak = this.atoms[kIdx];
          const invMk = 1 / ak.mass;
          const fk = ak.force;
          const result = tb.tripleTerm(
            ai,
            aj,
            ak,
            dijx,
            dijy,
            dijz,
            rij,
            neighborDx[nk],
            neighborDy[nk],
            neighborDz[nk],
            neighborR[nk],
          );
          if (!result) continue;
          energy += result.energy;
          if (fi) {
            fi.x += result.fix * invMi;
            fi.y += result.fiy * invMi;
            fi.z += result.fiz * invMi;
          }
          if (fj) {
            fj.x += result.fjx * invMj;
            fj.y += result.fjy * invMj;
            fj.z += result.fjz * invMj;
          }
          if (fk) {
            fk.x += result.fkx * invMk;
            fk.y += result.fky * invMk;
            fk.z += result.fkz * invMk;
          }
        }
      }
    }
    return energy;
  }

  /**
   * Many-body (EAM-family) kernel — two passes over pairs with a
   * per-atom embedding step in between.
   *
   *   Pass 1 (density): for each pair within cutoff, accumulate ψ(r_ij)
   *     into ρ_i and ψ(r_ji) into ρ_j.
   *   Embedding: for each atom compute F(ρ_i) (added to the energy) and
   *     cache F'(ρ_i) for the force pass.
   *   Pass 2 (forces): for each pair, combine the pair-direct φ'(r_ij)
   *     with the embedding chain rule (F'(ρ_i)+F'(ρ_j))·ψ'(r_ij) into
   *     one radial force scalar.
   *
   * Brute O(N²) with minimum-image — the LJ path's skinned Verlet list
   * isn't reused here yet. For the metallic supercells the app runs
   * (≲ few hundred atoms) this is fast enough; cell-listable later.
   */
  private computeManyBody(): number {
    const n = this.atoms.length;
    const box = this.box!;
    const mb = this.manyBodyPotential!;
    if (this.rho.length !== n) {
      this.rho = new Float64Array(n);
      this.dFdRho = new Float64Array(n);
    }
    this.rho.fill(0);

    const ljMaxSq = this.ljMaxCutoffSq;
    const rCutoffSq = this.rCutoff * this.rCutoff;
    const v = new Vector3();

    // Pass 1 — accumulate ρ_i over all in-cutoff pairs.
    for (let i = 0; i < n - 1; i++) {
      const ai = this.atoms[i];
      for (let j = i + 1; j < n; j++) {
        if (this.skipPair(i, j)) continue;
        const aj = this.atoms[j];
        v.subVectors(ai.position, aj.position);
        box.minimumImage(v);
        const rsq = v.lengthSq();
        if (rsq > ljMaxSq) continue;
        const sig = ai.sigma * aj.sigma;
        const rCutSq = rCutoffSq * sig;
        if (rsq >= rCutSq) continue;
        const d = mb.densityContribution(ai, aj, rsq, rCutSq);
        if (d) {
          this.rho[i] += d.psiOnI;
          this.rho[j] += d.psiOnJ;
        }
      }
    }

    // Embedding — F(ρ_i) into total energy, F'(ρ_i) cached for pass 2.
    let energy = 0;
    for (let i = 0; i < n; i++) {
      const e = mb.embedding(this.atoms[i], this.rho[i]);
      energy += e.F;
      this.dFdRho[i] = e.dFdRho;
    }

    // Pass 2 — pair energy + total radial force (pair + embedding).
    // Same mass-division convention as the LJ path: gx/gy/gz are
    // F·GF_CONV, divided by mass into atom.force so the stored value
    // is acceleration.
    for (let i = 0; i < n - 1; i++) {
      const ai = this.atoms[i];
      const invMi = 1 / ai.mass;
      const fi = ai.force;
      for (let j = i + 1; j < n; j++) {
        if (this.skipPair(i, j)) continue;
        const aj = this.atoms[j];
        v.subVectors(ai.position, aj.position);
        box.minimumImage(v);
        const rsq = v.lengthSq();
        if (rsq > ljMaxSq) continue;
        const sig = ai.sigma * aj.sigma;
        const rCutSq = rCutoffSq * sig;
        if (rsq >= rCutSq) continue;
        const r = mb.pairForce(ai, aj, rsq, rCutSq, this.dFdRho[i], this.dFdRho[j]);
        if (!r) continue;
        energy += r.energy;
        const invMj = 1 / aj.mass;
        const gx = r.fScalar * v.x;
        const gy = r.fScalar * v.y;
        const gz = r.fScalar * v.z;
        if (fi) {
          fi.x += gx * invMi;
          fi.y += gy * invMi;
          fi.z += gz * invMi;
        }
        const fj = aj.force;
        if (fj) {
          fj.x -= gx * invMj;
          fj.y -= gy * invMj;
          fj.z -= gz * invMj;
        }
      }
    }
    return energy;
  }

  /**
   * Lennard-Jones pass under periodic boundaries, accelerated with a
   * minimum-image Verlet neighbor list. The list — every atom's
   * neighbors within a skinned cutoff — is rebuilt every 10 steps and
   * just walked in between, so the O(N²) pair sweep is paid once per
   * interval instead of every step.
   *
   * The list only helps when there is a skin (list cutoff > force
   * cutoff). On a small box the LJ cutoff is capped by the
   * minimum-image limit, leaving no skin; there the list is rebuilt
   * every step — still correct, just no speed-up (and such cells are
   * small anyway). Big supercells, where speed matters, always have a
   * skin.
   */
  private computeLennardJonesPBC(): number {
    let energy = 0;
    const n = this.atoms.length;
    const box = this.box!;
    const ljMaxSq = this.ljMaxCutoffSq;
    const rCutoffSq = this.rCutoff * this.rCutoff;
    const rListSq = this.rList * this.rList;
    const v = new Vector3();

    this.ljPbcListAge--;
    const rebuild = this.ljPbcListAge <= 0;
    if (rebuild) {
      // A skin exists only if the largest force cutoff stays below the
      // minimum-image cap; otherwise list cutoff == force cutoff.
      const hasSkin = rCutoffSq * this.maxSigma * this.maxSigma < ljMaxSq;
      this.ljPbcListAge = hasSkin ? 10 : 1;
    }

    if (rebuild) {
      // Rebuild the neighbor list and run the force pass in one walk.
      let listIndex = 0;
      for (let i = 0; i < n - 1; i++) {
        this.pointer[i] = listIndex;
        const ai = this.atoms[i];
        const invMi = 1 / ai.mass;
        const fi = ai.force;
        for (let j = i + 1; j < n; j++) {
          if (this.skipPair(i, j)) continue;
          const aj = this.atoms[j];
          v.subVectors(ai.position, aj.position);
          box.minimumImage(v);
          const rsq = v.lengthSq();
          if (rsq > ljMaxSq) continue;
          const sig = ai.sigma * aj.sigma;
          if (rsq >= rListSq * sig) continue;
          this.neighborList[listIndex++] = j;
          if (rsq >= rCutoffSq * sig) continue;
          const result = this.evaluatePrimary(ai, aj, rsq, rCutoffSq * sig);
          if (!result) continue;
          energy += result.energy;
          this.virialLJ += result.virial;
          const invMj = 1 / aj.mass;
          // gx/gy/gz are F·GF_CONV; dividing by mass yields acceleration.
          const gx = result.fScalar * v.x;
          const gy = result.fScalar * v.y;
          const gz = result.fScalar * v.z;
          if (fi) {
            fi.x += gx * invMi;
            fi.y += gy * invMi;
            fi.z += gz * invMi;
          }
          const fj = aj.force;
          if (fj) {
            fj.x -= gx * invMj;
            fj.y -= gy * invMj;
            fj.z -= gz * invMj;
          }
        }
      }
      this.pointer[n - 1] = listIndex;
    } else {
      // Walk the saved list. Only reached when there is a skin, so the
      // force-cutoff test alone is binding (rCutoff·σ < the box cap).
      for (let i = 0; i < n - 1; i++) {
        const ai = this.atoms[i];
        const invMi = 1 / ai.mass;
        const fi = ai.force;
        const end = this.pointer[i + 1];
        for (let k = this.pointer[i]; k < end; k++) {
          const j = this.neighborList[k];
          if (this.skipPair(i, j)) continue;
          const aj = this.atoms[j];
          v.subVectors(ai.position, aj.position);
          box.minimumImage(v);
          const rsq = v.lengthSq();
          const sig = ai.sigma * aj.sigma;
          if (rsq >= rCutoffSq * sig) continue;
          const result = this.evaluatePrimary(ai, aj, rsq, rCutoffSq * sig);
          if (!result) continue;
          energy += result.energy;
          this.virialLJ += result.virial;
          const invMj = 1 / aj.mass;
          const gx = result.fScalar * v.x;
          const gy = result.fScalar * v.y;
          const gz = result.fScalar * v.z;
          if (fi) {
            fi.x += gx * invMi;
            fi.y += gy * invMi;
            fi.z += gz * invMi;
          }
          const fj = aj.force;
          if (fj) {
            fj.x -= gx * invMj;
            fj.y -= gy * invMj;
            fj.z -= gz * invMj;
          }
        }
      }
    }
    return energy;
  }

  /** Lennard-Jones pass for the finite (non-periodic) Verlet-list path. */
  private computeLennardJonesFinite(): number {
    const atomCount = this.atoms.length;
    const atomCount1 = atomCount - 1;
    const rCutoffSq = this.rCutoff * this.rCutoff;
    const rListSq = this.rList * this.rList;
    let energy = 0;
    const v = new Vector3();

    if (this.updateList) {
      // Cache positions for the next checkNeighborList() round, then
      // rebuild both the neighbor list and the in-cutoff force pass in
      // one walk so we don't re-pay the distance computation.
      for (let i = 0; i < atomCount; i++) {
        this.lastPositions[i].copy(this.atoms[i].position);
      }

      let listIndex = 0;
      for (let i = 0; i < atomCount1; i++) {
        this.pointer[i] = listIndex;
        let fxi = this.atoms[i].force?.x ?? 0;
        let fyi = this.atoms[i].force?.y ?? 0;
        let fzi = this.atoms[i].force?.z ?? 0;
        const invMi = 1 / this.atoms[i].mass;

        for (let j = i + 1; j < atomCount; j++) {
          if (this.skipPair(i, j)) continue;
          v.subVectors(this.atoms[i].position, this.atoms[j].position);
          const rsq = v.lengthSq();
          const sig = this.atoms[i].sigma * this.atoms[j].sigma;
          const invMj = 1 / this.atoms[j].mass;

          if (rsq < rListSq * sig) {
            this.neighborList[listIndex++] = j;

            if (rsq < rCutoffSq * sig) {
              const result = this.evaluatePrimary(this.atoms[i], this.atoms[j], rsq, rCutoffSq * sig);
              if (result) {
                energy += result.energy;
                this.virialLJ += result.virial;
                // gx/gy/gz hold F·GF_CONV in pre-mass-division units.
                // The bonded force loops (RadialBond / AngularBond /
                // TorsionalBond) divide by mass on the way in so
                // `atom.force` stores acceleration. We must do the
                // same here, or oxygens (m=16) see 16× the
                // electrostatic + LJ acceleration they should — which
                // is exactly what blows up TIP3P water.
                const gx = result.fScalar * v.x;
                const gy = result.fScalar * v.y;
                const gz = result.fScalar * v.z;
                fxi += gx * invMi;
                fyi += gy * invMi;
                fzi += gz * invMi;
                const force = this.atoms[j].force;
                if (force) {
                  force.x -= gx * invMj;
                  force.y -= gy * invMj;
                  force.z -= gz * invMj;
                }
              }
            }
          }
        }

        const force = this.atoms[i].force;
        if (force) {
          force.x = fxi;
          force.y = fyi;
          force.z = fzi;
        }
      }
      this.pointer[atomCount1] = listIndex;
      this.updateList = false;
    } else {
      // Fast path: walk the saved neighbor list instead of the O(N²)
      // pair sweep. Same mass-division convention as the slow path —
      // see comment above for why this matters for TIP3P water.
      for (let i = 0; i < atomCount1; i++) {
        let fxi = this.atoms[i].force?.x ?? 0;
        let fyi = this.atoms[i].force?.y ?? 0;
        let fzi = this.atoms[i].force?.z ?? 0;
        const invMi = 1 / this.atoms[i].mass;
        const end = this.pointer[i + 1];
        for (let k = this.pointer[i]; k < end; k++) {
          const j = this.neighborList[k];
          if (this.skipPair(i, j)) continue;
          v.subVectors(this.atoms[i].position, this.atoms[j].position);
          const rsq = v.lengthSq();
          const sig = this.atoms[i].sigma * this.atoms[j].sigma;
          const invMj = 1 / this.atoms[j].mass;

          if (rsq < rCutoffSq * sig) {
            const result = this.evaluatePrimary(this.atoms[i], this.atoms[j], rsq, rCutoffSq * sig);
            if (result) {
              energy += result.energy;
              this.virialLJ += result.virial;
              const gx = result.fScalar * v.x;
              const gy = result.fScalar * v.y;
              const gz = result.fScalar * v.z;
              fxi += gx * invMi;
              fyi += gy * invMi;
              fzi += gz * invMi;
              const force = this.atoms[j].force;
              if (force) {
                force.x -= gx * invMj;
                force.y -= gy * invMj;
                force.z -= gz * invMj;
              }
            }
          }
        }
        const force = this.atoms[i].force;
        if (force) {
          force.x = fxi;
          force.y = fyi;
          force.z = fzi;
        }
      }
    }

    return energy;
  }

  /**
   * O((N+V)²) Coulomb sweep over real atoms + virtual sites. Skips
   * fixed-fixed and removed-anything pairs (the integrator ignores
   * them); does NOT skip bonded pairs because intramolecular Coulomb
   * is part of the electrostatic field (TIP-family models only
   * exclude 1-2 / 1-3 LJ, not 1-2 Coulomb).
   *
   * Force application is uniform: each pair contributes `gx, gy, gz`
   * (force × GF_CONV) along the pair vector, then the recipient
   * routes that into its parent atom(s) and divides by mass. Real
   * atoms apply it to themselves; virtual sites decompose it
   * across their parents (see VirtualSite.applyForce).
   */
  /**
   * Coulomb pass over real atoms + virtual sites.
   *
   * Under periodic boundaries (`this.box` set) it uses the
   * damped-shifted-force (DSF) method of Fennell & Gezelter
   * (J. Chem. Phys. 124, 234104, 2006): the 1/r interaction is damped
   * by erfc(αr) and shifted so BOTH the energy and the force vanish
   * smoothly at the cutoff. That is the correct, Ewald-free way to sum
   * electrostatics under PBC — a bare minimum-image cutoff would have a
   * discontinuity that drifts the energy and disorders the crystal.
   *
   * Without a box it falls back to the original bare 1/r sum with a
   * short-range singularity floor (the finite-cluster path).
   */
  computeCoulomb(): number {
    let energy = 0;
    const entities = this.gatherChargeEntities();
    if (entities.length > 0 && entities.every((e) => e.dead)) {
      let checksum = 0;
      for (let k = 0; k < entities.length; k++) {
        const p = entities[k].position;
        checksum += (k + 1) * (p.x * 1.000003 + p.y * 0.999991 + p.z * 1.000017) + entities[k].charge;
      }
      const fc = this.frozenCoulomb;
      if (fc && fc.count === entities.length && fc.checksum === checksum) return fc.energy;
      // Compute it once (forces land on fixed atoms only — harmless) and keep it.
      const e = this.computeCoulombLive(entities);
      this.frozenCoulomb = { checksum, count: entities.length, energy: e };
      return e;
    }
    this.frozenCoulomb = null;
    return this.computeCoulombLive(entities);
  }

  private computeCoulombLive(entities: ChargeEntity[]): number {
    let energy = 0;
    // Exact Ewald path (periodic crystals). DSF below is the fallback.
    if (this.box && this.useEwald && this.ewald) {
      return this.ewald.compute(entities);
    }
    const v = new Vector3();
    const box = this.box;
    const rc = this.dsfCutoff;
    const rcSq = rc * rc;
    const alpha = this.dsfAlpha;
    const twoAOverSqrtPi = (2 * alpha) / Math.sqrt(Math.PI);
    for (let i = 0; i < entities.length - 1; i++) {
      const ei = entities[i];
      for (let j = i + 1; j < entities.length; j++) {
        const ej = entities[j];
        // Both anchored to fixed-or-removed parents → nothing to integrate.
        if (ei.dead && ej.dead) continue;
        // Skip intra-molecular pairs — for a rigid molecule the
        // intramolecular electrostatics are absorbed into the rigid
        // geometry; summing them on top produces huge net torques.
        if (ei.moleculeName && ei.moleculeName === ej.moleculeName) continue;
        if (Math.abs(ei.charge) + Math.abs(ej.charge) <= ZERO_TOLERANCE) continue;
        v.subVectors(ei.position, ej.position);
        if (box) box.minimumImage(v);
        const rsq = v.lengthSq();
        const qq = COULOMB_CONSTANT * ei.charge * ej.charge;
        let pairEnergy: number;
        let fij: number; // force scalar, already × GF_CONV: g = fij·v
        if (box) {
          // Damped-shifted-force.
          if (rsq >= rcSq || rsq < 1e-6) continue;
          const r = Math.sqrt(rsq);
          const erfcAr = erfc(alpha * r);
          const expAr = Math.exp(-alpha * alpha * rsq);
          pairEnergy = qq * (erfcAr / r - this.dsfEnergyShift + this.dsfForceShift * (r - rc));
          // Radial force F(r); vanishes at r = rc by construction.
          const forceRadial = qq * (erfcAr / rsq + (twoAOverSqrtPi * expAr) / r - this.dsfForceShift);
          fij = (forceRadial / r) * GF_CONVERSION_CONSTANT;
        } else {
          // Finite-cluster fallback: bare 1/r with a singularity floor.
          const rsqClamped = rsq < 0.25 ? 0.25 : rsq;
          pairEnergy = qq / Math.sqrt(rsqClamped);
          fij = (pairEnergy / rsqClamped) * GF_CONVERSION_CONSTANT;
        }
        energy += pairEnergy;
        this.virialEL += pairEnergy;
        const gx = fij * v.x;
        const gy = fij * v.y;
        const gz = fij * v.z;
        // +(gx,gy,gz) to entity i, −(gx,gy,gz) to entity j. Real atoms
        // route it to atom.force ÷ mass; virtual sites decompose it
        // across their parents — see VirtualSite.applyForce.
        ei.applyForce(gx, gy, gz);
        ej.applyForce(-gx, -gy, -gz);
      }
    }
    if (box) {
      // Wolf self-energy: −(erfc(αRc)/2Rc + α/√π)·Σqᵢ²·k. The DSF total
      // electrostatic energy is the pair sum PLUS this term. It is
      // position-independent — exerts no force, so the dynamics are
      // unchanged — but it is a real part of the energy: the potential-
      // energy readout (and the Madelung-constant check in
      // madelungCheck.ts) is wrong by this constant without it. Summed
      // over every charge entity, fixed ones included (they are real
      // lattice charges); removed atoms are a negligible edge case.
      let sumChargeSq = 0;
      for (const e of entities) sumChargeSq += e.charge * e.charge;
      energy -= COULOMB_CONSTANT * this.dsfSelfCoef * sumChargeSq;
    }
    return energy;
  }

  /**
   * Flatten real atoms + virtual sites into a uniform list of
   * charge-bearing entities for the Coulomb sweep. Each entity
   * exposes the minimum interface the kernel needs: position, charge,
   * a `dead` flag (so we can skip the obviously-zero-force pairs
   * before even computing distance), and an `applyForce` that knows
   * how to route accumulated force back to the integrator state.
   */
  private gatherChargeEntities(): ChargeEntity[] {
    if (!this.chargeEntities) {
      const out: ChargeEntity[] = [];
      const refresh: Array<() => void> = [];
      for (const a of this.atoms) {
        if (Math.abs(a.charge) <= ZERO_TOLERANCE) continue;
        const inv = 1 / a.mass;
        const e: ChargeEntity = {
          position: a.position,
          charge: a.charge,
          moleculeName: a.moleculeName,
          dead: a.removed === true || a.fixed === true,
          applyForce: (fx, fy, fz) => {
            const f = a.force;
            if (!f) return;
            f.x += fx * inv;
            f.y += fy * inv;
            f.z += fz * inv;
          },
        };
        out.push(e);
        refresh.push(() => {
          e.dead = a.removed === true || a.fixed === true;
        });
      }
      for (const vs of this.virtualSites) {
        const e: ChargeEntity = {
          position: vs.position,
          charge: vs.charge,
          moleculeName: vs.moleculeName,
          dead: vs.parents.every((p) => p.fixed === true || p.removed === true),
          applyForce: (fx, fy, fz) => vs.applyForce(fx, fy, fz),
        };
        out.push(e);
        refresh.push(() => {
          e.dead = vs.parents.every((p) => p.fixed === true || p.removed === true);
        });
      }
      this.chargeEntities = out;
      this.chargeEntityRefresh = refresh;
    }
    // `dead` is a LIVE property: an atom can be fixed (context menu, an
    // experiment that pins half the box) and released later. Frozen at cache
    // time, a released molecule stayed dead to the real-space loops — no
    // interactions among the released atoms at all — and an ice half fell
    // apart within 5 ps of being let go (measured 2026-10-07).
    for (const r of this.chargeEntityRefresh) r();
    return this.chargeEntities;
  }
}
